import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { offsetLabel, zonedStamp } from '../../inputs/lt-utc.js';
import { pdfBrandLogo } from '../../pdf-brand.js';
import { TOKENS } from '../../tokens.js';
import type { ReportBrand } from '../voyage/source.js';

/**
 * Kayıt PDF'lerinin ortak kabuğu (ADR 0062 §3; düzen ADR 0057 §7): A4 dikey; logo ve şirket adı,
 * başlık ve numara, durum işaretleri; iki sütunlu bilgi tablosu, bölüm başlıkları ve tablolar;
 * onay bilgisi (adım, ad, rol, zaman LT + UTC; imza basılmaz); altbilgide numara, basım zamanı,
 * Felixmate ve "sayfa x / y". Saf işlevler: gemi ve ofis aynı tanımı kullanır; pdfmake
 * istemcide, gömülü yazı tipiyle internetsiz çalışır. Metinler `recordPdf.*`.
 */

export type RecordPdfT = (key: string, options?: Record<string, unknown>) => string;

const FONT_SIZE = 9;
const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};
const FACTS: CustomTableLayout = { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 };

/** Boş değer `—`. */
export const pdfText = (value: unknown): string =>
  typeof value === 'string' && value.trim()
    ? value.trim()
    : typeof value === 'number'
      ? String(value)
      : '—';

/** Zaman LT ve UTC ayrı satırda: `15.10.2026 12:00 LT (UTC +03:00)` / `15.10.2026 09:00 UTC`. */
export function pdfLtUtc(iso: string | null | undefined, offsetMin: number | null): string {
  if (!iso) return '—';
  if (offsetMin === null) return `${zonedStamp(iso)} UTC`;
  return `${zonedStamp(iso, offsetMin)} LT (${offsetLabel(offsetMin)})\n${zonedStamp(iso)} UTC`;
}

export const pdfHeading = (value: string): Content => ({ text: value, style: 'section' });

/** İki sütunlu bilgi tablosu: [etiket, değer] çiftleri; boş değerler `—`. */
export function pdfFacts(pairs: readonly (readonly [string, string])[]): Content {
  const body: TableCell[][] = [];
  for (let i = 0; i < pairs.length; i += 2) {
    const [a, b] = [pairs[i], pairs[i + 1]];
    body.push([
      { text: a?.[0] ?? '', style: 'label' },
      { text: a?.[1] ?? '' },
      { text: b?.[0] ?? '', style: 'label' },
      { text: b?.[1] ?? '' },
    ]);
  }
  return { table: { widths: [90, '*', 90, '*'], body }, layout: FACTS };
}

/** Tam genişlikte uzun metin alanı (açıklama, kök neden). */
export function pdfBlock(label: string, value: string | null | undefined): Content[] {
  if (!value || !value.trim()) return [];
  return [
    { text: label, style: 'label', margin: [0, 4, 0, 1] },
    { text: value.trim(), margin: [0, 0, 0, 2] },
  ];
}

export function pdfTable(
  head: readonly string[],
  body: readonly (readonly (string | TableCell)[])[],
  widths: readonly (string | number)[],
): Content {
  return {
    table: {
      headerRows: 1,
      widths: [...widths],
      body: [head.map((h) => ({ text: h, bold: true })), ...body.map((row) => [...row])],
    },
    layout: LINES,
  };
}

/** Onay bilgisinin bir adımı: kim, hangi rolle, ne zaman (LT + UTC), not. */
export interface RecordPdfApproval {
  step: string;
  name: string | null;
  role: string | null;
  at: string | null;
  /** Saat farkı (dakika); ofis adımında `null` (yalnız UTC). */
  offsetMin: number | null;
  note?: string | null;
}

export interface RecordPdfInput {
  brand: ReportBrand | null;
  title: string;
  number: string;
  /** Sağ üstteki durum işaretleri (ör. "DRAFT – NOT SUBMITTED"). */
  marks: readonly string[];
  /** Belgenin konusu (PDF bilgisi; ör. gemi adı). */
  subject: string;
  content: readonly Content[];
  /**
   * Onay bilgisi. `null`: belge bir kaydın onayı değil, dökümdür (ör. Crew Profile, Record of
   * Employment; ADR 0131 Ek 3); onay bölümü basılmaz.
   */
  approvals: readonly RecordPdfApproval[] | null;
  /** Basım zamanı, UTC ISO. */
  printedAt: string;
  /** Onay bölümünün başlığı (verilmezse "Approval"; ör. tatbikat raporunda "Part E – Approval"). */
  approvalTitle?: string;
  /** Onay bölümünden sonra basılanlar (ör. sürüm bilgisi, ekler, değişiklik geçmişi). */
  trailer?: readonly Content[];
}

/** Ortak kabuk. */
export function recordPdf(t: RecordPdfT, input: RecordPdfInput): TDocumentDefinitions {
  const k = (key: string, options?: Record<string, unknown>) => t(`recordPdf.${key}`, options);
  const logo = pdfBrandLogo(input.brand, { fit: [48, 48], width: 56 });
  const content: Content[] = [
    {
      columns: [
        ...(logo ? [logo] : []),
        {
          width: '*',
          stack: [
            { text: input.brand?.name ?? '', style: 'company' },
            { text: input.title, style: 'title' },
            { text: k('number', { no: input.number }), style: 'muted' },
          ],
        },
        {
          width: 'auto',
          stack: input.marks.map((mark) => ({
            text: mark,
            style: 'mark',
            alignment: 'right' as const,
          })),
        },
      ],
      columnGap: 10,
      margin: [0, 0, 0, 8],
    },
    ...input.content,
  ];
  // Döküm (`approvals: null`): onay bölümü yok
  if (input.approvals !== null) {
    content.push(pdfHeading(input.approvalTitle ?? k('approval')));
    if (input.approvals.length === 0) {
      content.push({ text: k('noApproval'), style: 'muted' });
    } else {
      content.push(
        pdfTable(
          [k('step'), k('name'), k('role'), k('time'), k('note')],
          input.approvals.map((a) => [
            a.step,
            pdfText(a.name),
            pdfText(a.role),
            pdfLtUtc(a.at, a.offsetMin),
            pdfText(a.note ?? null),
          ]),
          [80, 80, 70, 110, '*'],
        ),
      );
    }
    content.push({ text: k('approvalNote'), style: 'muted', margin: [0, 4, 0, 0] });
  }
  if (input.trailer) content.push(...input.trailer);
  const printed = k('printed', { at: `${zonedStamp(input.printedAt)} UTC` });
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 36, 36, 44],
    defaultStyle: { font: 'Roboto', fontSize: FONT_SIZE, color: TOKENS['navy-900'] },
    info: {
      title: `${input.title} ${input.number}`,
      subject: input.subject,
      creator: 'Felixmate',
    },
    styles: {
      company: { fontSize: 10, bold: true, color: TOKENS['text-secondary'] },
      title: { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
      section: { fontSize: 10.5, bold: true, margin: [0, 10, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'], fontSize: 8 },
      mark: { bold: true, color: TOKENS['danger-fg'], fontSize: 9 },
      restricted: { italics: true, color: TOKENS['text-secondary'] },
    },
    content,
    footer: (page: number, pages: number) => ({
      columns: [
        {
          text: `${input.number} · ${printed} · Felixmate`,
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
        {
          text: k('page', { page, pages }),
          alignment: 'right',
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
      ],
      margin: [36, 12, 36, 0],
    }),
  };
}

/** Dosya adı: "ACC-2026-001 Accident Report.pdf". */
export function recordPdfFileName(number: string, title: string): string {
  return `${`${number} ${title}`.replace(/[^A-Za-z0-9 ._-]+/g, '-')}.pdf`;
}
